//src/components/PhilanthropyAdvisor.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download, Building2, Landmark, HandHeart, Banknote } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Module Philanthropie & Mécénat — Family Office
 * - Lit le client actif (table: family_office_active_client)
 * - Récupère profil + email (RPC get_user_email)
 * - Détermine leviers FR: prêt / dation / fondation / don usufruit-NP
 * - Calcule estimation fiscale simple (indicative)
 * - Exporte un PDF synthétique
 *
 * Hypothèses de colonnes:
 *   - profiles: id, first_name, last_name, country (ISO2), fiscal_regime, birth_year, philanthropy_active (bool)
 *   - family_office_active_client: family_office_id, active_client_id
 *
 * Facultatif:
 *   - RPC get_user_email(uid uuid) RETURNS text  -- voir snippet SQL plus bas
 *   - Table philanthropy_strategies (si présente) pour historiser
 */

const FR = "FR";
const REGIMES = {
  NONE: "NONE",
  FR_FORFAIT_6_5: "FR_FORFAIT_6_5",
  FR_PLUS_VALUE: "FR_PLUS_VALUE",
  UK_CGT_SIMPLE_20: "UK_CGT_SIMPLE_20",
  DE_PRIVATE_1Y_RULE: "DE_PRIVATE_1Y_RULE",
};

function prettyRegime(r, t) {
  switch (r) {
    case REGIMES.FR_FORFAIT_6_5:
      return t("philanthropyAdvisor.regimes.frFlatTax");
    case REGIMES.FR_PLUS_VALUE:
      return t("philanthropyAdvisor.regimes.frCapitalGain");
    case REGIMES.UK_CGT_SIMPLE_20:
      return t("philanthropyAdvisor.regimes.ukCgt");
    case REGIMES.DE_PRIVATE_1Y_RULE:
      return t("philanthropyAdvisor.regimes.dePrivate");
    case REGIMES.NONE:
    default:
      return t("philanthropyAdvisor.regimes.none");
  }
}

function joinName(p) {
  const fn = (p?.first_name || "").trim();
  const ln = (p?.last_name || "").trim();
  return [fn, ln].filter(Boolean).join(" ") || "—";
}

export default function PhilanthropyAdvisor() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "fr";
  const [foUser, setFoUser] = useState(null);
  const [activeClientId, setActiveClientId] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [clientEmail, setClientEmail] = useState("—");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  // 1) Charger l'utilisateur FO courant
  useEffect(() => {
    let ignore = false;
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!ignore && !error) setFoUser(data?.user || null);
      if (!ignore && (error || !data?.user)) setLoading(false);
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // 2) Charger le client actif pour ce FO
  useEffect(() => {
    if (!foUser?.id) return;
    let ignore = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("family_office_active_client")
          .select("active_client_id, last_switched_at")
          .eq("family_office_id", foUser.id)
          .order("last_switched_at", { ascending: false })
          .limit(1);
        if (error) throw error;
        const id = data?.[0]?.active_client_id || null;
        if (!ignore) setActiveClientId(id);
        // Aucun client actif : le chargement du profil ne démarrera jamais, on arrête le spinner
        if (!ignore && !id) setLoading(false);
      } catch (e) {
        console.error("load active client error:", e);
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [foUser?.id]);

  // 3) Profil client actif
  useEffect(() => {
    if (!activeClientId) return;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, country, fiscal_regime, birth_year, philanthropy_active")
          .eq("id", activeClientId)
          .single();
        if (error) throw error;
        if (!ignore) setClientProfile(data);
      } catch (e) {
        console.error("load profile error:", e);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [activeClientId]);

  // 4) Email via RPC get_user_email(uid) — fallback "—" si absente
  useEffect(() => {
    if (!activeClientId) return;
    let ignore = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_user_email", { uid: activeClientId });
        if (!ignore) setClientEmail(error ? "—" : (data || "—"));
      } catch {
        if (!ignore) setClientEmail("—");
      }
    })();
    return () => {
      ignore = true;
    };
  }, [activeClientId]);

  const fullName = useMemo(() => joinName(clientProfile), [clientProfile]);
  const country = clientProfile?.country || null;
  const regime = clientProfile?.fiscal_regime || REGIMES.NONE;

  // 5) Eligibilité & recommandations 🇫🇷
  const isFR = country === FR;

  const levers = useMemo(() => {
    const L = [];

    // 1. Prêt d'œuvres (général, focus FR pour déductibilité frais)
    L.push({
      key: "loan",
      title: t("philanthropyAdvisor.levers.loan.title"),
      icon: <Building2 className="w-5 h-5" />,
      enabled: true,
      details: [
        t("philanthropyAdvisor.levers.loan.detail1"),
        isFR ? t("philanthropyAdvisor.levers.loan.detail2Fr") : t("philanthropyAdvisor.levers.loan.detail2Other"),
        t("philanthropyAdvisor.levers.loan.detail3"),
      ],
    });

    // 2. Dation en paiement (France only)
    L.push({
      key: "dation",
      title: t("philanthropyAdvisor.levers.dation.title"),
      icon: <Landmark className="w-5 h-5" />,
      enabled: isFR,
      details: [
        t("philanthropyAdvisor.levers.dation.detail1"),
        t("philanthropyAdvisor.levers.dation.detail2"),
        t("philanthropyAdvisor.levers.dation.detail3"),
      ],
    });

    // 3. Fondation d’entreprise ou sous égide (général, fort focus FR)
    L.push({
      key: "foundation",
      title: t("philanthropyAdvisor.levers.foundation.title"),
      icon: <Banknote className="w-5 h-5" />,
      enabled: true,
      details: [
        t("philanthropyAdvisor.levers.foundation.detail1"),
        t("philanthropyAdvisor.levers.foundation.detail2"),
        t("philanthropyAdvisor.levers.foundation.detail3"),
      ],
    });

    // 4. Don avec usufruit / nue-propriété (France only)
    L.push({
      key: "usufruct",
      title: t("philanthropyAdvisor.levers.usufruct.title"),
      icon: <HandHeart className="w-5 h-5" />,
      enabled: isFR,
      details: [
        t("philanthropyAdvisor.levers.usufruct.detail1"),
        t("philanthropyAdvisor.levers.usufruct.detail2"),
        t("philanthropyAdvisor.levers.usufruct.detail3"),
      ],
    });

    return L;
  }, [isFR, t]);

  // 6) Estimation gain fiscal indicatif (très simplifié, pédagogique)
  const estimatedTaxBenefit = useMemo(() => {
    // NB: on n’a pas la valeur collection consolidée ici.
    // On fait une heuristique illustrative basée sur le régime.
    if (!isFR) return 0;

    if (regime === REGIMES.FR_FORFAIT_6_5) {
      // Hypothèse: optimisation via dons/ prêts réduit l'assiette future
      return 5000; // purely indicative placeholder
    }
    if (regime === REGIMES.FR_PLUS_VALUE) {
      // Hypothèse: privilégier dons, prêts, fondation -> optimisation PV future
      return 15000;
    }
    return 3000;
  }, [isFR, regime]);

  // 7) Enregistrer une stratégie (si la table existe)
  const saveStrategy = async () => {
    setSaving(true);
    try {
      const recommendation = {
        country,
        regime,
        levers: levers.map((l) => ({ key: l.key, enabled: l.enabled })),
        note,
      };

      // tentative d'insertion — si la table n’existe pas ou RLS bloque, on passe en silencieux
      const { error } = await supabase.from("philanthropy_strategies").insert({
        profile_id: activeClientId,
        country,
        fiscal_regime: regime,
        recommendation,
        potential_tax_saving: estimatedTaxBenefit,
      });
      if (error) {
        console.warn("philanthropy_strategies insert skipped:", error.message);
        alert(t("philanthropyAdvisor.alerts.createdNotStored"));
      } else {
        alert(t("philanthropyAdvisor.alerts.createdStored"));
      }
    } catch (e) {
      console.warn("saveStrategy:", e);
      alert(t("philanthropyAdvisor.alerts.createdNotStored"));
    } finally {
      setSaving(false);
    }
  };

  // 8) Export PDF
  const exportPdf = async () => {
    const node = document.getElementById("phila-plan");
    if (!node) return;
    try {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfW, pdfH);
      const filename = `philanthropy_plan_${fullName.replace(/\s+/g, "_") || "client"}.pdf`;
      pdf.save(filename);
    } catch (e) {
      console.error("PDF export error:", e);
      alert(t("philanthropyAdvisor.alerts.pdfError"));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin h-6 w-6 border-t-2 border-b-2 border-black rounded-full" />
      </div>
    );
  }

  if (!activeClientId || !clientProfile) {
    return (
      <div className="p-6 border rounded bg-yellow-50 text-yellow-900">
        {t("philanthropyAdvisor.noActiveClient")}
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light">{t("philanthropyAdvisor.title")}</h1>
          <p className="text-gray-600">{t("philanthropyAdvisor.subtitle")}</p>
        </div>
        <button
          onClick={exportPdf}
          className="inline-flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-50"
          title={t("philanthropyAdvisor.actions.exportPdfTitle")}
        >
          <Download className="w-4 h-4" /> {t("philanthropyAdvisor.actions.exportPdfShort")}
        </button>
      </div>

      {/* Carte client */}
      <div className="mb-6 grid md:grid-cols-2 gap-4">
        <div className="border rounded p-4">
          <div className="text-sm text-gray-500">{t("philanthropyAdvisor.clientCard.client")}</div>
          <div className="text-lg font-medium">{fullName}</div>
          <div className="text-sm text-gray-700">{clientEmail || "—"}</div>
        </div>
        <div className="border rounded p-4">
          <div className="text-sm text-gray-500">{t("philanthropyAdvisor.clientCard.taxRegime")}</div>
          <div className="text-lg font-medium">{prettyRegime(regime, t)}</div>
          <div className="text-sm text-gray-700">{t("philanthropyAdvisor.clientCard.taxCountry", { country: country || "—" })}</div>
        </div>
      </div>

      {/* Bloc exportable */}
      <div id="phila-plan" className="bg-white border rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-light">{t("philanthropyAdvisor.plan.title")}</h2>
          <div className="text-sm text-gray-600">{t("philanthropyAdvisor.plan.estimatedBenefit")}: {" "}
            <span className="font-medium">{new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(estimatedTaxBenefit)}</span>
          </div>
        </div>

        {/* Leviers */}
        <div className="grid md:grid-cols-2 gap-4">
          {levers.map((lev) => (
            <div
              key={lev.key}
              className={`p-4 border rounded ${lev.enabled ? "bg-gray-50" : "bg-white opacity-60"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {lev.icon}
                <div className="font-medium">{lev.title}</div>
                <span
                  className={`ml-auto text-xs px-2 py-0.5 rounded border ${
                    lev.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}
                >
                  {lev.enabled ? t("philanthropyAdvisor.eligibility.eligible") : t("philanthropyAdvisor.eligibility.notApplicable")}
                </span>
              </div>
              <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
                {lev.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Intérêts au-delà de la fiscalité */}
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">{t("philanthropyAdvisor.beyondTax.title")}</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="border rounded p-3">
              <div className="font-medium mb-1">{t("philanthropyAdvisor.beyondTax.legitimacy.title")}</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>{t("philanthropyAdvisor.beyondTax.legitimacy.point1")}</li>
                <li>{t("philanthropyAdvisor.beyondTax.legitimacy.point2")}</li>
              </ul>
            </div>
            <div className="border rounded p-3">
              <div className="font-medium mb-1">{t("philanthropyAdvisor.beyondTax.network.title")}</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>{t("philanthropyAdvisor.beyondTax.network.point1")}</li>
                <li>{t("philanthropyAdvisor.beyondTax.network.point2")}</li>
              </ul>
            </div>
            <div className="border rounded p-3">
              <div className="font-medium mb-1">{t("philanthropyAdvisor.beyondTax.value.title")}</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>{t("philanthropyAdvisor.beyondTax.value.point1")}</li>
                <li>{t("philanthropyAdvisor.beyondTax.value.point2")}</li>
              </ul>
            </div>
            <div className="border rounded p-3">
              <div className="font-medium mb-1">{t("philanthropyAdvisor.beyondTax.transmission.title")}</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>{t("philanthropyAdvisor.beyondTax.transmission.point1")}</li>
                <li>{t("philanthropyAdvisor.beyondTax.transmission.point2")}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Note conseillèr(e) */}
        <div className="mt-6">
          <label className="block text-sm font-medium mb-1">{t("philanthropyAdvisor.note.label")}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
            placeholder={t("philanthropyAdvisor.note.placeholder")}
          />
        </div>

        {/* Disclaimer */}
        <div className="mt-6 text-xs text-gray-600 border-t pt-3">
          <b>{t("philanthropyAdvisor.disclaimer.title")}</b>{" "}{t("philanthropyAdvisor.disclaimer.text")}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={saveStrategy}
          disabled={saving}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {saving ? t("philanthropyAdvisor.actions.saving") : t("philanthropyAdvisor.actions.createPlan")}
        </button>
        <button
          onClick={exportPdf}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          {t("philanthropyAdvisor.actions.exportPdf")}
        </button>
      </div>
    </div>
  );
}
